import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  IconButton,
  Select,
  FormControl,
  MenuItem,
  Pagination,
} from "@mui/material";
import StorageIcon from "@mui/icons-material/Storage";
import AddIcon from "@mui/icons-material/Add";
import RestoreIcon from "@mui/icons-material/Restore";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";

const Backup = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [backupData, setBackupData] = useState([]);
  const [loading, setLoading] = useState({
    list: false,
    backup: false,
    restore: null,
    download: null,
  });

  // Pagination States
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalBackups, setTotalBackups] = useState(0);

  const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
      Authorization: `Token ${token}`,
    },
  });

  const fetchBackupList = async () => {
    setLoading((prev) => ({ ...prev, list: true }));
    try {
      const response = await axiosInstance.get(
        `/database-backup/list/?page=${page + 1}&page_size=${rowsPerPage}`
      );
      const { results, count } = response.data;
      setBackupData(results.backups);
      setTotalBackups(count);
    } catch (error) {
      console.error("Error fetching backup data:", error);
    } finally {
      setLoading((prev) => ({ ...prev, list: false }));
    }
  };

  useEffect(() => {
    fetchBackupList();
  }, [page, rowsPerPage]);

  const handleCreateBackup = async () => {
    setLoading((prev) => ({ ...prev, backup: true }));
    try {
      const response = await axiosInstance.post("/database-backup/backup/");

      if (response?.data?.file_name) {
        const downloadUrl = `${process.env.REACT_APP_API_URL}/database-backup/download/${response.data.file_name}/`;
        window.location.href = downloadUrl;
        fetchBackupList();
      }
    } catch (error) {
      console.error("Error creating backup:", error);
    } finally {
      setLoading((prev) => ({ ...prev, backup: false }));
    }
  };

  const handleRestoreBackup = async (fileName) => {
    setLoading((prev) => ({ ...prev, restore: fileName }));
    try {
      const formData = new FormData();
      formData.append("file_name", fileName);

      await axiosInstance.post("/database-backup/restore/", formData);
      alert(t("hr.database_restored"));
    } catch (error) {
      console.error("Error restoring backup:", error);
    } finally {
      setLoading((prev) => ({ ...prev, restore: null }));
    }
  };

  const handleDownloadBackup = async (fileName) => {
    setLoading((prev) => ({ ...prev, download: fileName }));
    try {
      const downloadUrl = `${process.env.REACT_APP_API_URL}/database-backup/download/${fileName}/`;
      window.location.href = downloadUrl;
    } catch (error) {
      console.error("Error downloading backup:", error);
    } finally {
      setLoading((prev) => ({ ...prev, download: null }));
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  return (
    <Paper
      sx={{
        width: "100%",
        minHeight: "350px",
        borderTop: "4px solid #f4a531",
        boxShadow: 3,
        p: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid lightGray",
          pb: 1,
          mb: 2,
        }}
      >
        <Box display="flex" flexDirection="row" gap={1} alignItems="center">
          <StorageIcon fontSize="large" color="primary" />
          <Typography variant="h6" fontWeight="bold">
            {t("hr.backup_management")}
          </Typography>
        </Box>
        <Box>
          <IconButton
            onClick={fetchBackupList}
            disabled={loading.list || loading.backup}
            sx={{ mr: 2 }}
          >
            {loading.list ? (
              <CircularProgress size={24} />
            ) : (
              <RefreshIcon color="primary" />
            )}
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateBackup}
            disabled={loading.backup}
            sx={{
              backgroundColor: loading.backup ? "gray" : "#a7e2f9",
              color: "black",
              ":hover": {
                backgroundColor: "#a7e2f9",
                color: "gray",
              },
            }}
          >
            {loading.backup ? (
              <CircularProgress size={24} sx={{ color: "white" }} />
            ) : (
              t("hr.create_backup")
            )}
          </Button>
        </Box>
      </Box>

      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            borderBottom: "1px solid lightGray",
            pb: 1,
            mb: 1,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {t("hr.backup_files")}
          </Typography>
          <Typography variant="h6" fontWeight="bold">
            {t("hr.action")}
          </Typography>
        </Box>

        <Box>
          {loading.list ? (
            <Typography sx={{ textAlign: "center", color: "gray", mt: 2 }}>
              {t("hr.loading_backups")}
            </Typography>
          ) : backupData.length ? (
            backupData.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid lightGray",
                  p: 1,
                  mb: 1,
                  borderRadius: 1,
                  backgroundColor: "#f9f9f9",
                }}
              >
                <Typography variant="body2" sx={{ color: "#1e8cbe" }}>
                  {item.name}
                </Typography>
                <Box>
                  <Button
                    size="small"
                    startIcon={
                      loading.restore === item.name ? (
                        <CircularProgress size={16} />
                      ) : (
                        <RestoreIcon />
                      )
                    }
                    disabled={loading.restore !== null}
                    sx={{
                      backgroundColor:
                        loading.restore === item.name ? "gray" : "#a7e2f9",
                      color: "black",
                      "&.Mui-disabled": {
                        backgroundColor: "lightGray",
                        color: "black",
                      },
                      ":hover": {
                        backgroundColor: "#a7e2f9",
                        color: "black",
                      },
                      margin: "0px 10px",
                    }}
                    onClick={() => handleRestoreBackup(item.name)}
                  >
                    {t("hr.restore")}
                  </Button>
                  <Button
                    size="small"
                    startIcon={
                      loading.download === item.name ? (
                        <CircularProgress size={16} />
                      ) : (
                        <DownloadIcon />
                      )
                    }
                    disabled={loading.download !== null}
                    sx={{
                      backgroundColor:
                        loading.download === item.name ? "gray" : "#a7e2f9",
                      color: "black",
                      "&.Mui-disabled": {
                        backgroundColor: "lightGray",
                        color: "black",
                      },
                      ":hover": {
                        backgroundColor: "#a7e2f9",
                        color: "black",
                      },
                    }}
                    onClick={() => handleDownloadBackup(item.name)}
                  >
                    {t("hr.download", "Download")}
                  </Button>
                </Box>
              </Box>
            ))
          ) : (
            <Typography sx={{ textAlign: "center", color: "gray", mt: 2 }}>
              {t("hr.no_backup_files")}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Pagination Controls */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={4}
        mb={2}
      >
        {/* Rows Per Page Selector */}
        <Box display="flex" alignItems="center">
          <Typography variant="body2" mr={2}>
            {t("pharmacy.rowsPerPage")}:
          </Typography>
          <FormControl variant="outlined" size="small">
            <Select
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              sx={{
                width: 80,
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
            >
              {[10, 25, 50, 100, 500, 1000].map((rows) => (
                <MenuItem key={rows} value={rows}>
                  {rows}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Pagination Component */}
        <Pagination
          count={Math.ceil(totalBackups / rowsPerPage)}
          page={page}
          onChange={handlePageChange}
          variant="outlined"
          color="primary"
          showFirstButton
          showLastButton
        />
      </Box>
    </Paper>
  );
};

export default Backup;
